import { useCallback, useEffect, useRef, useState } from "react";
import { useLang } from "../context/LangContext";

const MAX_RECORDING_MS = 15000;
const TRANSCRIBE_ENDPOINT = "/.netlify/functions/transcribe-voice";
const SPEAK_ENDPOINT = "/.netlify/functions/speak-answer";
const MAX_TTS_CHARACTERS = 1000;
const VOICE_PLAYBACK_RATE = 1.18;

function getVoiceLanguage(lang) {
  return lang === "ar" ? "ar" : "en";
}

function getSupportedMimeType() {
  if (typeof MediaRecorder === "undefined" || typeof MediaRecorder.isTypeSupported !== "function") {
    return "";
  }

  const candidates = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/mp4",
  ];

  return candidates.find((mimeType) => MediaRecorder.isTypeSupported(mimeType)) || "";
}

function readBlobAsBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = typeof reader.result === "string" ? reader.result : "";
      resolve(result.includes(",") ? result.split(",").pop() : result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

function stopStream(stream) {
  stream?.getTracks?.().forEach((track) => track.stop());
}

export function useAssistantVoice({ onTranscript } = {}) {
  const { lang, t } = useLang();
  const language = getVoiceLanguage(lang);
  const mediaRecorderRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const recordingChunksRef = useRef([]);
  const recordingSessionRef = useRef(0);
  const recordingTimeoutRef = useRef(null);
  const audioRef = useRef(null);
  const audioUrlRef = useRef("");
  const speakingSessionRef = useRef(0);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [speechLoading, setSpeechLoading] = useState(false);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [voiceError, setVoiceError] = useState("");

  const clearRecordingTimer = useCallback(() => {
    if (recordingTimeoutRef.current) {
      clearTimeout(recordingTimeoutRef.current);
      recordingTimeoutRef.current = null;
    }
  }, []);

  const cleanupRecording = useCallback(() => {
    clearRecordingTimer();
    stopStream(mediaStreamRef.current);
    mediaStreamRef.current = null;
    mediaRecorderRef.current = null;
    setRecording(false);
  }, [clearRecordingTimer]);

  const cleanupAudio = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = "";
      audioRef.current = null;
    }

    if (audioUrlRef.current) {
      URL.revokeObjectURL(audioUrlRef.current);
      audioUrlRef.current = "";
    }
  }, []);

  const stopSpeaking = useCallback(() => {
    speakingSessionRef.current += 1;
    cleanupAudio();
    setSpeechLoading(false);
    setAudioPlaying(false);
  }, [cleanupAudio]);

  const clearVoiceError = useCallback(() => {
    setVoiceError("");
  }, []);

  const transcribeBlob = useCallback(
    async (blob, sessionId) => {
      if (!blob.size || recordingSessionRef.current !== sessionId) return;

      setTranscribing(true);
      setVoiceError("");

      try {
        const audioBase64 = await readBlobAsBase64(blob);
        const response = await fetch(TRANSCRIBE_ENDPOINT, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            audioBase64,
            mimeType: blob.type || "audio/webm",
            language,
          }),
        });
        const result = await response.json().catch(() => ({}));

        if (!response.ok || !result.text) {
          throw new Error(result.error || "Voice transcription failed.");
        }

        if (recordingSessionRef.current === sessionId && typeof onTranscript === "function") {
          await onTranscript(result.text);
        }
      } catch {
        if (recordingSessionRef.current === sessionId) {
          setVoiceError(t("assistant_voice_error"));
        }
      } finally {
        if (recordingSessionRef.current === sessionId) {
          setTranscribing(false);
        }
      }
    },
    [language, onTranscript, t]
  );

  const stopListening = useCallback(() => {
    clearRecordingTimer();
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      recorder.stop();
      return;
    }
    cleanupRecording();
  }, [clearRecordingTimer, cleanupRecording]);

  const startListening = useCallback(async () => {
    setVoiceError("");

    if (recording || transcribing) {
      if (recording) stopListening();
      return;
    }

    if (
      typeof navigator === "undefined" ||
      !navigator.mediaDevices?.getUserMedia ||
      typeof MediaRecorder === "undefined"
    ) {
      setVoiceError(t("assistant_voice_error"));
      return;
    }

    recordingSessionRef.current += 1;
    const sessionId = recordingSessionRef.current;
    recordingChunksRef.current = [];

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (recordingSessionRef.current !== sessionId) {
        stopStream(stream);
        return;
      }

      const mimeType = getSupportedMimeType();
      const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);

      mediaStreamRef.current = stream;
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (event) => {
        if (event.data?.size) {
          recordingChunksRef.current.push(event.data);
        }
      };

      recorder.onerror = () => {
        if (recordingSessionRef.current === sessionId) {
          setVoiceError(t("assistant_voice_error"));
        }
        cleanupRecording();
        setTranscribing(false);
      };

      recorder.onstop = () => {
        const chunks = recordingChunksRef.current;
        const type = chunks[0]?.type || mimeType || "audio/webm";
        const blob = new Blob(chunks, { type });
        cleanupRecording();
        transcribeBlob(blob, sessionId);
      };

      recorder.start();
      setRecording(true);
      recordingTimeoutRef.current = setTimeout(() => {
        if (recordingSessionRef.current === sessionId && mediaRecorderRef.current?.state !== "inactive") {
          mediaRecorderRef.current.stop();
        }
      }, MAX_RECORDING_MS);
    } catch {
      if (recordingSessionRef.current === sessionId) {
        setVoiceError(t("assistant_voice_error"));
      }
      cleanupRecording();
      setTranscribing(false);
    }
  }, [cleanupRecording, recording, stopListening, t, transcribeBlob, transcribing]);

  const speak = useCallback(
    async (text, options = {}) => {
      const cleanText = String(text || "").trim().slice(0, MAX_TTS_CHARACTERS);
      if (!cleanText) return;

      stopSpeaking();
      speakingSessionRef.current += 1;
      const sessionId = speakingSessionRef.current;
      setVoiceError("");
      setSpeechLoading(true);

      try {
        const response = await fetch(SPEAK_ENDPOINT, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            text: cleanText,
            language,
          }),
        });

        if (!response.ok) {
          throw new Error("Voice speech failed.");
        }

        const audioBlob = await response.blob();
        if (speakingSessionRef.current !== sessionId) return;

        const audioUrl = URL.createObjectURL(audioBlob);
        const audio = new Audio(audioUrl);
        audio.playbackRate = VOICE_PLAYBACK_RATE;
        audioRef.current = audio;
        audioUrlRef.current = audioUrl;

        audio.onended = () => {
          if (speakingSessionRef.current !== sessionId) return;
          cleanupAudio();
          setAudioPlaying(false);
          setSpeechLoading(false);
        };
        audio.onerror = () => {
          if (speakingSessionRef.current !== sessionId) return;
          cleanupAudio();
          setAudioPlaying(false);
          setSpeechLoading(false);
          if (!options.silentError) {
            setVoiceError(t("assistant_speech_error"));
          }
        };

        await audio.play();
        if (speakingSessionRef.current !== sessionId) return;
        setAudioPlaying(true);
        setSpeechLoading(false);
      } catch {
        if (speakingSessionRef.current === sessionId) {
          cleanupAudio();
          setSpeechLoading(false);
          setAudioPlaying(false);
          if (!options.silentError) {
            setVoiceError(t("assistant_speech_error"));
          }
        }
      }
    },
    [cleanupAudio, language, stopSpeaking, t]
  );

  useEffect(() => {
    return () => {
      recordingSessionRef.current += 1;
      clearRecordingTimer();
      cleanupRecording();
      stopSpeaking();
    };
  }, [clearRecordingTimer, cleanupRecording, stopSpeaking]);

  return {
    listening: recording || transcribing,
    recording,
    transcribing,
    speaking: speechLoading || audioPlaying,
    speechLoading,
    voiceError,
    startListening,
    stopListening,
    speak,
    stopSpeaking,
    clearVoiceError,
  };
}
